import { Button, Modal } from "@/components/basic";
import DeleteHour from "@/components/hours/DeleteHour";
import ResourceHoursHelpTooltip from "@/components/hours/ResourceHoursHelpTooltip";
import CreateOrEditHourRule from "@/components/hours/rule/CreateOrEditHourRule";
import { NonSharedHourRulesTable } from "@/components/hours/rule/NonSharedHourRulesTable";
import { SharedHourRulesTable } from "@/components/hours/rule/SharedHourRulesTable";
import LoadingSpinner from "@/components/util/LoadingSpinner";
import { useBulkHourRules } from "@/hooks/useHours";
import { useToggle } from "@/hooks/useToggle";
import { api } from "@/lib/api/api";
import { IHour, IResource, Office } from "@gettactic/api";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import { ScrollArea } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import { IoArrowDown, IoArrowForward, IoAdd as Plus } from "react-icons/io5";

type Props = {
  office: Office;
  resources?: IResource[];
  title: string;
};

export type IHourWithResName = IHour & {
  resName?: string;
};

export function groupSharedKey(hour: IHourWithResName) {
  return `${hour.is_available ? "true" : "false"}_${hour.since}_${hour.until}_${hour.start_time}_${hour.end_time}_${
    hour.name
  }`;
}

export type ConfirmOperationModal = {
  operation: "delete" | "edit";
  applyTo: IHourWithResName[];
};

export default function ListHourRules({ title, office, resources }: Props) {
  const queryClient = useQueryClient();
  const [restHoursTable, toggleRestHoursTable] = useToggle();
  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const [isCreateOpen, toggleCreateOpen] = useToggle(false);
  const hours = useBulkHourRules(office, resources);
  const invalidateQuery = useCallback(
    () => queryClient.invalidateQueries(["hours.rules", office.id]),
    [office]
  );

  const [shared, rest, inGroups] = useMemo(() => {
    const total = resources ? resources.length : 0;
    const data = hours.data ?? [];
    const inGroups: { [idx: string]: IHourWithResName[] } = {};
    const shared: IHourWithResName[] = [];
    let rest: IHourWithResName[] = [];
    data.forEach((group) => {
      if (group) {
        group.elements.forEach((hour) => {
          const res = (resources ?? []).find((x) => x.id === hour.resource_id);
          const hourWithResName: IHourWithResName = {
            ...hour,
            resName: res?.name ?? "-"
          };
          inGroups[groupSharedKey(hour)] = inGroups[groupSharedKey(hour)] || [];
          inGroups[groupSharedKey(hour)].push(hourWithResName);
        });
      }
    });
    Object.keys(inGroups).forEach((group) => {
      if (inGroups[group].length > 0 && inGroups[group].length >= total) {
        // we only save 1 instance
        shared.push(inGroups[group][0]);
      } else {
        rest = rest.concat(inGroups[group]);
      }
    });
    return [shared, rest, inGroups];
  }, [hours]);

  return (
    <div className="col-span-12">
      <div className="mt-4 flex items-center justify-between border-b border-gray-200 pb-2">
        <div className={"flex items-center justify-center"}>
          <h3 className="text-2xl font-medium leading-6 text-gray-900">
            {title} {resources ? <ResourceHoursHelpTooltip /> : null}
          </h3>
          {!resources ? (
            <div className="my-2 text-base font-normal text-tertiary">
              Define any holidays or office closures for this location.
              Reservations cannot be made when an office is closed.{" "}
              <a
                className="cursor-pointer font-bold text-secondary [.tactic-v2_&]:text-secondary-foreground"
                target="_blank"
                rel="noreferrer noopener"
                href="https://gettactic.zendesk.com/hc/en-us/articles/27811185241371-Manage-Office-Hours"
              >
                <span>Learn more</span>{" "}
                <ExternalLinkIcon className="inline-flex h-5 w-5" />
              </a>
            </div>
          ) : null}
        </div>
        <div>
          <Button
            type="button"
            variant="primary"
            size="base"
            layout="squared"
            className="ml-4 inline-flex"
            onClick={() => {
              toggleCreateOpen();
              setOperationModal(null);
            }}
            iconLeft={Plus}
          >
            <span className="mx-2">Add Hours</span>
          </Button>
        </div>
      </div>

      {resources && resources.length > 1 && shared.length ? (
        <h3 className="mt-6 mb-4 font-bold text-lg text-secondary [.tactic-v2_&]:text-secondary-foreground">
          Shared Custom Hours among the Selected Resources ({resources.length})
        </h3>
      ) : null}

      <Modal
        title={resources ? "Custom Hours" : "Holidays & Closures"}
        opened={isCreateOpen}
        onClose={() => toggleCreateOpen()}
      >
        <CreateOrEditHourRule
          hours={
            operationModal?.operation === "edit"
              ? operationModal?.applyTo
              : undefined
          }
          key={
            operationModal?.operation === "edit"
              ? operationModal?.applyTo.map((x) => x.id).join(",")
              : isCreateOpen
                ? "new"
                : "new-close"
          }
          onClose={() => {
            toggleCreateOpen();
            setOperationModal(null);
          }}
          office={office}
          resources={resources}
          invalidateQuery={invalidateQuery}
        />
      </Modal>
      <Modal
        title="Delete Item"
        opened={
          operationModal
            ? operationModal.applyTo.length > 0 &&
              operationModal.operation === "delete"
            : false
        }
        onClose={() => setOperationModal(null)}
        size="lg"
      >
        {operationModal ? (
          <DeleteHour
            invalidateQuery={invalidateQuery}
            hours={operationModal.applyTo}
            onClose={() => setOperationModal(null)}
            officeId={office.id}
          />
        ) : null}
      </Modal>

      {hours.isSuccess && shared.length + rest.length === 0 ? (
        <div className="flex flex-col flex-nowrap overflow-hidden">
          <ScrollArea type="auto" offsetScrollbars className="h-full">
            <div className=" flex flex-col items-center justify-center">
              {resources ? (
                <div className="flex max-w-prose flex-col items-center">
                  <Image
                    src="https://cdn.gettactic.com/website/images/custom-hours-empty.png"
                    width={313}
                    height={215}
                    alt="No office hours"
                  />
                  <h3 className="max-w-prose place-self-start text-center text-lg font-bold text-gray-800">
                    Get started clicking on the &ldquo;Add Hours&ldquo; button
                  </h3>
                </div>
              ) : (
                <div className="my-6 flex max-w-prose flex-col items-center">
                  <Image
                    src="https://cdn.gettactic.com/website/images/office-holidays.svg"
                    width={170}
                    height={179}
                    alt="No holidays"
                  />
                  <h3 className="max-w-prose place-self-start text-center text-lg font-bold text-gray-800">
                    Get started clicking on the &ldquo;Add Hours&ldquo; button
                  </h3>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      ) : null}

      {hours.isLoading ? <LoadingSpinner /> : null}

      {shared?.length ? (
        <SharedHourRulesTable
          setOperationalModal={(x) => {
            if (x && x.operation === "edit") {
              toggleCreateOpen();
            }
            setOperationModal(x);
          }}
          loading={false}
          data={shared}
          inGroups={inGroups}
          updateFunction={async (hour, newWeight) => {
            await Promise.all(
              inGroups[groupSharedKey(hour)].map((hour) =>
                api.client.hours.put(
                  office.id,
                  hour.id,
                  { ...hour, weight: newWeight },
                  { tz: office.time_zone }
                )
              )
            );
          }}
          invalidateQuery={invalidateQuery}
        />
      ) : null}

      {resources && resources.length > 1 && rest.length ? (
        <>
          <h3
            onClick={toggleRestHoursTable}
            className="mt-6 mb-4 cursor-pointer text-lg font-bold text-secondary [.tactic-v2_&]:text-secondary-foreground"
          >
            {restHoursTable ? (
              <IoArrowDown className="inline h-4 w-4" />
            ) : (
              <IoArrowForward className="inline h-4 w-4" />
            )}{" "}
            Non Shared Hours ({rest.length})
          </h3>
          {restHoursTable ? (
            <NonSharedHourRulesTable
              setOperationalModal={(x) => {
                if (x && x.operation === "edit") {
                  toggleCreateOpen();
                }
                setOperationModal(x);
              }}
              loading={false}
              data={rest}
              invalidateQuery={invalidateQuery}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
